<template>
  <ZapiModalShell
    :visible="mostrar"
    @update:visible="$emit('closeModal')"
    :title="mermaEdit && mermaEdit.id ? 'Editar Merma' : 'Registrar Merma'"
    width="50rem"
    :loading="enviando"
    :disabled="enviando"
    @confirm="guardarMerma"
  >
    <div class="p-fluid grid mt-2">
      <!-- Sucursal -->
      <div class="field col-12 md:col-6">
        <label for="sucursal">Sucursal <span class="text-danger">*</span></label>
        <DropdownSucursal v-model="merma.sucursal_id" @change="fetchLotesProducto" />
      </div>

      <!-- Producto -->
      <div class="field col-12 md:col-6">
        <label for="producto">Producto <span class="text-danger">*</span></label>
        <DropdownProducto v-model="merma.producto_id" :es-insumo="1" @change="fetchLotesProducto" />
      </div>

      <!-- Lote Afectado / Vencimiento (Visible si hay lotes disponibles) -->
      <div class="field col-12 md:col-6" v-if="lotesDisponibles && lotesDisponibles.length > 0">
        <label for="lote">Lote Afectado / Vencimiento <span class="text-danger">*</span></label>
        <Dropdown
          id="lote"
          v-model="loteSeleccionado"
          :options="lotesDisponibles"
          optionLabel="lote"
          placeholder="Seleccione un lote..."
          :loading="cargandoLotes"
          @change="onLoteChange"
        >
          <template #option="slotProps">
            <div>
              <strong>Lote: {{ slotProps.option.lote }}</strong>
              <small class="block text-500">
                Vence: {{ slotProps.option.fecha_vence || 'S/F' }} &middot; Disponibles: {{ slotProps.option.cantidad }}g 
                <span v-if="slotProps.option.gramaje_unidad > 0">
                  ({{ Math.floor(slotProps.option.cantidad / slotProps.option.gramaje_unidad) }} porciones)
                </span>
              </small>
            </div>
          </template>
          <template #value="slotProps">
            <div v-if="slotProps.value">
              <strong>{{ slotProps.value.lote }}</strong> &middot; Vence: {{ slotProps.value.fecha_vence || 'S/F' }}
              <span v-if="slotProps.value.gramaje_unidad > 0"> ({{ slotProps.value.gramaje_unidad }}g/porción)</span>
            </div>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
        </Dropdown>
      </div>

      <!-- Tipo de Merma -->
      <div class="field col-12 md:col-6">
        <label for="tipo_merma">Tipo de Merma <span class="text-danger">*</span></label>
        <DropdownTipoMerma v-model="merma.tipo_merma_id" />
      </div>

      <!-- Fecha -->
      <div class="field col-12 md:col-6">
        <label for="fecha">Fecha <span class="text-danger">*</span></label>
        <Calendar v-model="merma.fecha" dateFormat="dd/mm/yy" :showIcon="true" placeholder="Seleccione una fecha" />
      </div>

      <!-- CONDICIONAL: Si el Lote Seleccionado es Porcionado -->
      <template v-if="esLotePorcionado">
        <div class="field col-12 md:col-6">
          <label for="porciones">Cantidad (Porciones) <span class="text-danger">*</span></label>
          <InputNumber
            v-model="porciones"
            :min="1"
            placeholder="Ej: 2"
            @input="onPorcionesInput"
          />
          <small class="text-500 block mt-1" v-if="loteSeleccionado && porciones > 0">
            Equivale a: <strong>{{ (porciones * loteSeleccionado.gramaje_unidad).toFixed(2) }} gramos</strong>
            ({{ porciones }} porción(es) x {{ loteSeleccionado.gramaje_unidad }}g)
          </small>
        </div>
      </template>

      <!-- CONDICIONAL: Si No hay Lote Porcionado / Producto a Granel -->
      <template v-else>
        <!-- Cantidad Gramos -->
        <div class="field col-12 md:col-6">
          <label for="cantidad_gramos" :class="{'text-500': merma.cantidad_unidad > 0}">
            Cantidad (Gramos) <span class="text-danger" v-if="!merma.cantidad_unidad || merma.cantidad_unidad === 0">*</span>
          </label>
          <InputNumber 
            v-model="merma.cantidad_gramos" 
            :minFractionDigits="2" 
            :maxFractionDigits="2" 
            placeholder="Ej: 500.00"
            :disabled="merma.cantidad_unidad > 0"
            @input="onGramosInput"
          />
        </div>

        <!-- Cantidad Unidad -->
        <div class="field col-12 md:col-6">
          <label for="cantidad_unidad" :class="{'text-500': merma.cantidad_gramos > 0}">
            Cantidad (Unidad) <span class="text-danger" v-if="!merma.cantidad_gramos || merma.cantidad_gramos === 0">*</span>
          </label>
          <InputNumber 
            v-model="merma.cantidad_unidad" 
            :minFractionDigits="0" 
            placeholder="Ej: 1"
            :disabled="merma.cantidad_gramos > 0"
            @input="onUnidadInput"
          />
        </div>
      </template>

      <!-- Estado -->
      <div class="field col-12 md:col-6">
        <label for="estado">Estado <span class="text-danger">*</span></label>
        <Dropdown
          id="estado"
          v-model="merma.estado"
          :disabled="!merma.id"
          :options="estados"
          optionLabel="label"
          optionValue="value"
          placeholder="Seleccione un estado"
        >
          <template #value="slotProps">
            <span v-if="slotProps.value !== null" :class="'customer-badge ' + claseEstado[slotProps.value]">
              {{ estados.find(e => e.value === slotProps.value)?.label }}
            </span>
          </template>
          <template #option="slotProps">
            <span :class="'customer-badge ' + claseEstado[slotProps.option.value]">
              {{ slotProps.option.label }}
            </span>
          </template>
        </Dropdown>
      </div>

      <!-- Observación -->
      <div class="field col-12">
        <label for="observacion">Observación</label>
        <Textarea v-model="merma.observacion" rows="3" placeholder="Detalles o motivo de la merma..." />
      </div>
    </div>
  </ZapiModalShell>
</template>

<script>
import ZapiModalShell from '@/components/shared/ZapiModalShell.vue';
import DropdownSucursal from '@/components/shared/Dropdown/DropdownSucursal.vue';
import DropdownProducto from '@/components/shared/Dropdown/DropdownProducto.vue';
import DropdownTipoMerma from '@/components/shared/Dropdown/DropdownTipoMerma.vue';
import Dropdown from 'primevue/dropdown';
import MermaService from '@/service/MermaService';
import ProductService from '@/service/ProductService';

export default {
  name: 'MermaCreate',
  components: {
    ZapiModalShell,
    DropdownSucursal,
    DropdownProducto,
    DropdownTipoMerma,
    Dropdown
  },
  props: {
    // Propiedad para controlar si el modal se muestra o no
    mostrar: {
      type: Boolean,
      default: false
    },
    // Propiedad que recibe los datos de la merma si estamos en modo "Editar"
    mermaEdit: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      // Objeto reactivo que se bindea al formulario
      merma: {
        sucursal_id: null,
        producto_id: null,
        tipo_merma_id: null,
        fecha: null,
        cantidad_gramos: 0,
        cantidad_unidad: 0,
        lote: null,
        estado: 1,
        observacion: ''
      },
      lotesDisponibles: [],
      loteSeleccionado: null,
      porciones: 0,
      cargandoLotes: false,
      enviando: false, // Controla el estado del botón guardar
      estados: [
        { label: 'Activo', value: 1 },
        { label: 'Inactivo', value: 0 }
      ],
      claseEstado: {
        1: 'status-qualified',
        0: 'status-unqualified'
      },
      mermaService: null,
      productService: null
    };
  },
  computed: {
    /**
     * Determina si el lote seleccionado corresponde a un producto porcionado
     * (proveniente de una orden de producción con gramaje_unidad > 0).
     */
    esLotePorcionado() {
      return this.loteSeleccionado && parseFloat(this.loteSeleccionado.gramaje_unidad || 0) > 0;
    }
  },
  watch: {
    /**
     * Vigila cuando la propiedad 'mostrar' cambia.
     * Si el modal se abre (newVal = true), ejecutamos cargarDatos()
     */
    mostrar(newVal) {
      if (newVal) {
        this.cargarDatos();
      }
    }
  },
  created() {
    // Instanciamos los servicios necesarios
    this.mermaService = new MermaService();
    this.productService = new ProductService();
  },
  methods: {
    /**
     * Prepara el formulario al abrir el modal.
     * Si viene un objeto para editar, lo asigna al estado local. 
     * Si no, limpia el formulario para crear una nueva merma.
     */
    async cargarDatos() {
      this.lotesDisponibles = [];
      this.loteSeleccionado = null;
      this.porciones = 0;

      if (this.mermaEdit && this.mermaEdit.id) {
        // MODO EDICIÓN
        this.merma = { ...this.mermaEdit };
        
        // Mapeamos el estado de manera robusta: si es 1, "1" o "ACTIVO" lo ponemos en 1, sino en 0 (Inactivo)
        const est = this.merma.estado;
        this.merma.estado = (est === 1 || est === '1' || String(est).toUpperCase() === 'ACTIVO') ? 1 : 0;
        
        // El componente Calendar de PrimeVue requiere un objeto Date nativo de JS
        if (this.merma.fecha) {
          this.merma.fecha = new Date(this.merma.fecha);
        }

        // Consultamos lotes para el producto y sucursal editados
        await this.fetchLotesProducto();
        if (this.merma.lote && this.lotesDisponibles.length > 0) {
          const lotePrevio = this.lotesDisponibles.find(l => l.lote === this.merma.lote);
          if (lotePrevio) {
            this.loteSeleccionado = lotePrevio;
            if (parseFloat(lotePrevio.gramaje_unidad || 0) > 0 && this.merma.cantidad_gramos > 0) {
              this.porciones = Math.round(this.merma.cantidad_gramos / lotePrevio.gramaje_unidad);
            }
          }
        }
      } else {
        // MODO CREACIÓN (Limpiar campos)
        this.merma = {
          sucursal_id: null,
          producto_id: null,
          tipo_merma_id: null,
          fecha: new Date(), // Asigna la fecha de hoy por defecto
          cantidad_gramos: 0,
          cantidad_unidad: 0,
          lote: null,
          estado: 1,
          observacion: ''
        };
      }
    },

    /**
     * Consulta los lotes disponibles para la combinación Sucursal + Producto seleccionada.
     */
    async fetchLotesProducto() {
      this.lotesDisponibles = [];
      this.loteSeleccionado = null;
      this.porciones = 0;
      this.merma.lote = null;

      if (!this.merma.producto_id || !this.merma.sucursal_id) {
        return;
      }

      this.cargandoLotes = true;
      try {
        const res = await this.productService.getLotesDisponibles(this.merma.producto_id, this.merma.sucursal_id);
        if (res && res.lotes) {
          this.lotesDisponibles = res.lotes;
          // Si solo existe 1 lote disponible, se auto-selecciona por practicidad
          if (this.lotesDisponibles.length === 1) {
            this.loteSeleccionado = this.lotesDisponibles[0];
            this.onLoteChange();
          }
        }
      } catch (error) {
        console.error("Error al consultar lotes disponibles:", error);
        this.lotesDisponibles = [];
      } finally {
        this.cargandoLotes = false;
      }
    },

    /**
     * Maneja el cambio de selección del Lote.
     */
    onLoteChange() {
      if (this.loteSeleccionado) {
        this.merma.lote = this.loteSeleccionado.lote;
        if (this.esLotePorcionado) {
          this.porciones = 1;
          this.recalcularGramosPorcion();
        }
      } else {
        this.merma.lote = null;
        this.porciones = 0;
      }
    },

    /**
     * Recalcula la cantidad de gramos en base a las porciones ingresadas y el gramaje por unidad.
     */
    onPorcionesInput() {
      this.recalcularGramosPorcion();
    },
    onGramosInput(event) {
      if (event && event.value && event.value > 0) {
        this.merma.cantidad_unidad = 0;
      }
    },
    onUnidadInput(event) {
      if (event && event.value && event.value > 0) {
        this.merma.cantidad_gramos = 0;
      }
    },

    recalcularGramosPorcion() {
      if (this.esLotePorcionado && this.loteSeleccionado) {
        const gramaje = parseFloat(this.loteSeleccionado.gramaje_unidad || 0);
        const cantPorciones = parseFloat(this.porciones || 0);
        this.merma.cantidad_gramos = cantPorciones * gramaje;
        this.merma.cantidad_unidad = cantPorciones;
      }
    },

    /**
     * Valida que los campos obligatorios estén llenos antes de enviar la petición.
     * Retorna true si es válido, false si falta algún dato.
     */
    validarFormulario() {
      if (!this.merma.sucursal_id || !this.merma.producto_id || !this.merma.tipo_merma_id || !this.merma.fecha) {
        this.$toast.add({ 
          severity: 'warn', 
          summary: 'Advertencia', 
          detail: 'Por favor complete todos los campos obligatorios (*)', 
          life: 3000 
        });
        return false;
      }

      // Si existen lotes disponibles para el producto, requerir la selección de uno
      if (this.lotesDisponibles.length > 0 && !this.loteSeleccionado) {
        this.$toast.add({ 
          severity: 'warn', 
          summary: 'Advertencia', 
          detail: 'Por favor seleccione el lote o fecha de vencimiento afectado', 
          life: 3000 
        });
        return false;
      }

      // Validar cantidad ingresada
      if (this.esLotePorcionado) {
        if (!this.porciones || this.porciones <= 0) {
          this.$toast.add({ 
            severity: 'warn', 
            summary: 'Advertencia', 
            detail: 'Debe ingresar al menos 1 porción para la merma', 
            life: 3000 
          });
          return false;
        }
      } else {
        if ((!this.merma.cantidad_gramos || this.merma.cantidad_gramos <= 0) && (!this.merma.cantidad_unidad || this.merma.cantidad_unidad <= 0)) {
          this.$toast.add({ 
            severity: 'warn', 
            summary: 'Advertencia', 
            detail: 'Debe ingresar una cantidad válida en gramos o unidades', 
            life: 3000 
          });
          return false;
        }
      }

      return true;
    },

    /**
     * Llama al servicio para hacer POST o PUT según corresponda.
     * Se activa cuando el usuario hace clic en el botón "Guardar".
     */
    async guardarMerma() {
      if (this.enviando) return;
      
      // Asegurar cálculo previo de porciones a gramos si corresponde
      if (this.esLotePorcionado) {
        this.recalcularGramosPorcion();
      }

      if (!this.validarFormulario()) return;

      this.enviando = true;
      try {
        // Asignar datos adicionales del lote al payload
        const payload = {
          ...this.merma,
          lote: this.loteSeleccionado ? this.loteSeleccionado.lote : this.merma.lote,
          fecha_vence: this.loteSeleccionado ? this.loteSeleccionado.fecha_vence : null,
          porciones: this.esLotePorcionado ? this.porciones : null
        };

        let response;
        if (payload.id) {
          // Si tiene ID, es una actualización (PUT)
          response = await this.mermaService.update(payload);
        } else {
          // Si no tiene ID, es un registro nuevo (POST)
          response = await this.mermaService.create(payload);
        }

        // Mostramos el mensaje de éxito
        this.$toast.add({ 
          severity: 'success', 
          summary: 'Éxito', 
          detail: response.message || 'Merma guardada correctamente', 
          life: 3000 
        });
        
        // Notificamos al componente padre (MermaList) para que recargue la tabla
        this.$emit('actualizarListado');
        // Cerramos este modal
        this.$emit('closeModal');
        
      } catch (error) {
        console.error("Error al guardar la merma:", error);
        this.$toast.add({ 
          severity: 'error', 
          summary: 'Error', 
          detail: error.response?.data?.message || 'Ocurrió un error al guardar la merma', 
          life: 3000 
        });
      } finally {
        this.enviando = false;
      }
    }
  }
};
</script>

<style scoped>
.text-danger {
  color: #ff5252;
  font-weight: bold;
}

/* Estilos de badges copiados de MermaList para que se vean igual en el Dropdown */
.customer-badge {
    border-radius: 2px;
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}
.customer-badge.status-qualified {
    background: #C8E6C9;
    color: #256029;
}
.customer-badge.status-unqualified {
    background: #FFCDD2;
    color: #C63737;
}
</style>
